import { format, parseISO, isValid } from "date-fns";
import { FileText, Eye, Loader2, X } from "lucide-react";
import { Button } from "../../components/ui/button";

interface DocThumbProps {
  name: string;
  url: string;
  onRemove?: () => void;
  onOpen?: () => void | Promise<void>;
  opening?: boolean;
  className?: string;
  updatedAt?: string;
}

/**
 * Square (~aspect-square) document tile: image preview for image files,
 * PDF page preview for PDFs, extension-coloured badge otherwise.
 * Hover surfaces Eye (open) and X (remove).
 * Used by Insurance Policies and Shipments to show uploaded docs uniformly.
 */
export function DocThumb({ name, url, onRemove, onOpen, opening, className, updatedAt }: DocThumbProps) {
  const ext = (name.split(".").pop() || "").toLowerCase();
  const isImage = ["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext);
  const isPdf = ext === "pdf";
  const badgeColor =
    ext === "pdf" ? "bg-red-500/10 text-red-700" :
    ["xlsx", "xls", "csv"].includes(ext) ? "bg-emerald-500/10 text-emerald-700" :
    ext === "docx" || ext === "doc" ? "bg-blue-500/10 text-blue-700" :
    "bg-muted text-muted-foreground";
  const updatedLabel = (() => {
    if (!updatedAt) return null;
    const parsed = parseISO(updatedAt);
    return isValid(parsed) ? format(parsed, "dd MMM HH:mm") : null;
  })();

  const handleOpen = () => {
    if (opening) return;
    if (onOpen) {
      void onOpen();
      return;
    }
    window.open(url, "_blank");
  };

  return (
    <div className={`group relative rounded-lg border overflow-hidden bg-background ${className || ""}`}>
      <button
        type="button"
        onClick={handleOpen}
        disabled={opening}
        className="block w-full aspect-square bg-muted/30 flex items-center justify-center overflow-hidden"
      >
        {isImage ? (
          <img src={url} alt={name} className="w-full h-full object-cover" />
        ) : isPdf && url ? (
          <iframe
            src={`${url}#toolbar=0&navpanes=0&scrollbar=0`}
            title={name}
            className="w-full h-full pointer-events-none border-0 bg-white"
          />
        ) : (
          <div className="flex flex-col items-center gap-2 p-3">
            <FileText className="h-10 w-10 text-muted-foreground/60" />
            <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded ${badgeColor}`}>{ext || "FILE"}</span>
          </div>
        )}
      </button>
      <div className="px-2 py-1.5 border-t bg-background">
        <div className="text-[11px] font-medium truncate" title={name}>{name}</div>
        {updatedLabel && <div className="text-[10px] text-muted-foreground">{updatedLabel}</div>}
      </div>
      {opening && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/70">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}
      <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition">
        <Button variant="secondary" size="icon" className="h-6 w-6" disabled={opening} onClick={(e) => { e.stopPropagation(); handleOpen(); }}>
          {opening ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
        </Button>
        {onRemove && (
          <Button variant="secondary" size="icon" className="h-6 w-6 text-destructive" onClick={(e) => { e.stopPropagation(); onRemove(); }}>
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default DocThumb;
